import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ReviewsTable from "./ReviewsTable";
import GetStartedButton from "./GetStartedButton";

export default function ReviewsModal({
  open,
  onOpenChange,
  webDomain,
  affiliateLink,
  companyName,
}) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && webDomain) {
      // Track modal opened event
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "view_reviews_clicked", {
          event_category: "Reviews",
          company_name: companyName,
          web_domain: webDomain,
        });
      }

      // Reset state and load reviews JSON file
      setLoading(true);
      setReviews([]);

      // Create AbortController for cleanup
      const abortController = new AbortController();

      fetch(`/reviews/${webDomain}.json`, { signal: abortController.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Reviews not found");
          }
          return response.json();
        })
        .then((data) => {
          setReviews(data);
          setLoading(false);
        })
        .catch((error) => {
          // Don't update state if fetch was aborted
          if (error.name === "AbortError") {
            return;
          }
          console.error("Error loading reviews:", error);
          setReviews([]);
          setLoading(false);
        });

      // Cleanup function to abort fetch on unmount or dependency change
      return () => {
        abortController.abort();
      };
    } else if (!open) {
      // Reset state when modal closes
      setLoading(true);
      setReviews([]);
    }
  }, [open, webDomain, companyName]);

  const handleClose = () => {
    // Track close button click
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "reviews_modal_closed", {
        event_category: "Reviews",
        company_name: companyName,
        web_domain: webDomain,
      });
    }
    onOpenChange(false);
  };


  const handleScroll = () => {
    // Track table scroll - throttled to avoid too many events
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "reviews_table_scrolled", {
        event_category: "Reviews",
        company_name: companyName,
        web_domain: webDomain,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[900px] h-[85vh] flex flex-col p-2 sm:p-6">
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="pb-4 border-b">
            <h2 className="text-2xl font-bold text-gray-900">
              User Reviews - {companyName}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Showing verified reviews rated 4 stars and above
            </p>
          </div>

          {/* Reviews Table */}
          <div className="flex-1 overflow-hidden mt-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Loading reviews...</div>
              </div>
            ) : (
              <ReviewsTable
                reviews={reviews}
                onScroll={handleScroll}
                affiliateLink={affiliateLink}
                companyName={companyName}
              />
            )}
          </div>

          {/* Get Started Button */}
          <div className="pt-4 border-t mt-4">
            <GetStartedButton
              affiliateLink={affiliateLink}
              companyName={companyName}
              eventSource="reviews"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
